import { useEffect, useState } from "react";
import { SHOW_FEED } from "../../config/site";
import { ExternalLink, Instagram, LogOut, Music2, Pencil, Plus, RefreshCw, Trash2, Youtube } from "lucide-react";
import { adminFetch } from "../../lib/adminApi";
import { mediaUrl } from "../../lib/media";

const PLATFORM_ICONS = { instagram: Instagram, tiktok: Music2, youtube: Youtube };
const SOCIAL_FIELDS = [
  { key: "instagram", label: "Instagram", placeholder: "https://instagram.com/yourhandle" },
  { key: "tiktok", label: "TikTok", placeholder: "https://tiktok.com/@yourhandle" },
  { key: "youtube", label: "YouTube", placeholder: "https://youtube.com/@yourchannel" },
];
const IG_REDIRECT = `${process.env.REACT_APP_BACKEND_URL}/api/instagram/callback`;

const inputCls =
  "w-full border border-white/15 bg-ink px-4 py-3 text-sm text-paper placeholder:text-paper/30 transition-colors focus:border-acid focus:outline-none";

export default function Dashboard({ onNew, onEdit, onLogout }) {
  const [tab, setTab] = useState("projects");
  const [projects, setProjects] = useState([]);
  const [posts, setPosts] = useState([]);
  const [events, setEvents] = useState([]);
  const [socials, setSocials] = useState({ instagram: "", tiktok: "", youtube: "" });
  const [socialsStatus, setSocialsStatus] = useState("");
  const [ig, setIg] = useState({ configured: false, connected: false });
  const [igAppId, setIgAppId] = useState("");
  const [igAppSecret, setIgAppSecret] = useState("");
  const [igMsg, setIgMsg] = useState("");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    if (tab === "socials") {
      Promise.all([
        adminFetch("/api/settings/socials").then((r) => (r.ok ? r.json() : {})),
        adminFetch("/api/admin/instagram/status").then((r) => (r.ok ? r.json() : {})),
      ])
        .then(([s, status]) => {
          setSocials({ instagram: "", tiktok: "", youtube: "", ...s });
          setIg({ configured: false, connected: false, ...status });
        })
        .catch(() => {})
        .finally(() => setLoading(false));
      return;
    }
    const endpoint =
      tab === "projects"
        ? "/api/admin/projects"
        : tab === "events"
          ? "/api/admin/events"
          : "/api/admin/posts";
    adminFetch(endpoint)
      .then((r) => (r.ok ? r.json() : []))
      .then((list) =>
        tab === "projects" ? setProjects(list) : tab === "events" ? setEvents(list) : setPosts(list)
      )
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(load, [tab]);

  const del = async (kind, item, label) => {
    if (!window.confirm(`Delete "${label}"? This cannot be undone.`)) return;
    await adminFetch(`/api/admin/${kind}/${item.id}`, { method: "DELETE" });
    load();
  };

  const saveSocials = async () => {
    setSocialsStatus("Saving…");
    const res = await adminFetch("/api/admin/settings/socials", {
      method: "PUT",
      body: JSON.stringify(socials),
    });
    setSocialsStatus(res.ok ? "Saved — icons are live on the site." : "Save failed — try again");
    if (res.ok) setTimeout(() => setSocialsStatus(""), 4000);
  };

  const saveIgApp = async () => {
    setIgMsg("Saving…");
    const res = await adminFetch("/api/admin/settings/instagram-app", {
      method: "PUT",
      body: JSON.stringify({ app_id: igAppId.trim(), app_secret: igAppSecret.trim() }),
    });
    if (res.ok) {
      setIgMsg("App keys saved.");
      setIg((s) => ({ ...s, configured: true }));
      setIgAppSecret("");
    } else {
      setIgMsg("Save failed — check both fields");
    }
  };

  const connectIg = async () => {
    setIgMsg("Opening Instagram…");
    const res = await adminFetch("/api/admin/instagram/connect-url", { method: "POST" });
    const data = await res.json().catch(() => ({}));
    if (res.ok && data.url) {
      window.location.href = data.url;
    } else {
      setIgMsg(typeof data.detail === "string" ? data.detail : "Could not start connection");
    }
  };

  const syncNow = async () => {
    setIgMsg("Syncing…");
    const res = await adminFetch("/api/admin/instagram/sync", { method: "POST" });
    const data = await res.json().catch(() => ({}));
    setIgMsg(res.ok ? `Synced ${data.synced ?? 0} posts.` : "Sync failed — try reconnecting");
    load();
  };

  const items = tab === "projects" ? projects : tab === "events" ? events : posts;

  return (
    <div data-testid="admin-dashboard" className="min-h-svh bg-ink text-paper">
      <header className="border-b border-white/10">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-4 py-5 sm:px-6">
          <div className="flex items-center gap-4">
            <img src="/images/logo.png" alt="Triple O Creative Industries" className="h-8 w-auto" />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-paper/50">
              Studio Dashboard
            </span>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/"
              data-testid="admin-view-site"
              className="flex items-center gap-2 border border-white/15 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-paper/70 transition-colors hover:border-acid hover:text-acid"
            >
              <ExternalLink className="h-3.5 w-3.5" /> View Site
            </a>
            {tab !== "socials" && (
              <button
                type="button"
                data-testid="admin-new-item"
                onClick={() => onNew(tab === "projects" ? "project" : tab === "events" ? "event" : "post")}
                className="flex items-center gap-2 bg-acid px-4 py-2.5 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-ink transition-colors hover:bg-paper"
              >
                <Plus className="h-3.5 w-3.5" />{" "}
                {tab === "projects" ? "New Project" : tab === "events" ? "New Event" : "New Post"}
              </button>
            )}
            <button
              type="button"
              data-testid="admin-logout"
              onClick={onLogout}
              className="flex items-center gap-2 border border-white/15 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-paper/70 transition-colors hover:border-red-400 hover:text-red-400"
            >
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
        <div className="mx-auto flex max-w-6xl flex-wrap gap-2 px-4 pb-4 sm:px-6">
          {[
            ["projects", "Projects"],
            ["events", "Events"],
            ["posts", "Feed Posts"],
            ["socials", "Social Links"],
          ]
            .filter(([id]) => SHOW_FEED || id !== "posts")
            .map(([id, label]) => (
            <button
              key={id}
              type="button"
              data-testid={`admin-tab-${id}`}
              onClick={() => setTab(id)}
              aria-pressed={tab === id}
              className={`border px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] transition-colors ${
                tab === id
                  ? "border-acid bg-acid/10 text-acid"
                  : "border-white/15 text-paper/50 hover:border-white/40 hover:text-paper"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
        {tab === "socials" ? (
          <div data-testid="admin-socials" className="max-w-xl">
            <p className="font-display text-xl font-bold uppercase tracking-tight">Social Links</p>
            <p className="mt-3 text-sm leading-relaxed text-fog">
              Paste each profile URL once it's live. Filled-in platforms automatically appear as
              icons in the footer and the contact section. Empty ones stay hidden.
            </p>
            {loading ? (
              <p className="mt-8 font-mono text-xs uppercase tracking-[0.3em] text-paper/50">Loading…</p>
            ) : (
              <div className="mt-8 space-y-5">
                {SOCIAL_FIELDS.map(({ key, label, placeholder }) => {
                  const Icon = PLATFORM_ICONS[key];
                  return (
                    <div key={key}>
                      <label htmlFor={`social-${key}`} className="mb-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-paper/50">
                        <Icon className="h-3.5 w-3.5 text-acid" /> {label}
                      </label>
                      <input
                        id={`social-${key}`}
                        data-testid={`social-input-${key}`}
                        value={socials[key]}
                        onChange={(e) => setSocials((s) => ({ ...s, [key]: e.target.value }))}
                        className={inputCls}
                        placeholder={placeholder}
                      />
                    </div>
                  );
                })}
                {socialsStatus && (
                  <p data-testid="socials-save-status" className="font-mono text-[11px] uppercase tracking-[0.15em] text-acid">
                    {socialsStatus}
                  </p>
                )}
                <button
                  type="button"
                  data-testid="socials-save-button"
                  onClick={saveSocials}
                  className="bg-acid px-10 py-4 font-mono text-xs font-bold uppercase tracking-[0.25em] text-ink transition-colors hover:bg-paper"
                >
                  Save Links
                </button>
              </div>
            )}

            {SHOW_FEED && (
            <div data-testid="admin-instagram" className="mt-12 border-t border-white/10 pt-8">
              <p className="flex items-center gap-2 font-display text-xl font-bold uppercase tracking-tight">
                <Instagram className="h-5 w-5 text-acid" /> Instagram Auto-Feed
              </p>
              <p className="mt-3 text-sm leading-relaxed text-fog">
                Connect once and every new Instagram post — plus posts you're tagged in — syncs to
                your Feed page automatically. Nothing else to do after this.
              </p>

              <div className="mt-6 border border-white/10 bg-panel p-5">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-paper/50">
                  Step 1 — In your Meta app, whitelist this exact redirect URL:
                </p>
                <p data-testid="ig-redirect-url" className="mt-3 break-all bg-ink px-3 py-2.5 font-mono text-[11px] leading-relaxed text-acid">
                  {IG_REDIRECT}
                </p>
              </div>

              {!ig.connected && (
                <div className="mt-6 space-y-5">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-paper/50">
                    Step 2 — Paste your app keys:
                  </p>
                  <div>
                    <label htmlFor="ig-app-id" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-paper/50">
                      Instagram App ID
                    </label>
                    <input
                      id="ig-app-id"
                      data-testid="ig-app-id-input"
                      value={igAppId}
                      onChange={(e) => setIgAppId(e.target.value)}
                      className={inputCls}
                      placeholder="e.g. 1234567890123456"
                    />
                  </div>
                  <div>
                    <label htmlFor="ig-app-secret" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-paper/50">
                      Instagram App Secret
                    </label>
                    <input
                      id="ig-app-secret"
                      data-testid="ig-app-secret-input"
                      type="password"
                      value={igAppSecret}
                      onChange={(e) => setIgAppSecret(e.target.value)}
                      className={inputCls}
                      placeholder={ig.configured ? "Saved — enter again only to replace" : "Paste the app secret"}
                    />
                  </div>
                  <button
                    type="button"
                    data-testid="ig-save-app"
                    onClick={saveIgApp}
                    disabled={!igAppId.trim() || !igAppSecret.trim()}
                    className="border border-acid px-8 py-3.5 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-acid transition-colors hover:bg-acid hover:text-ink disabled:opacity-40"
                  >
                    Save App Keys
                  </button>
                </div>
              )}

              {ig.configured && !ig.connected && (
                <button
                  type="button"
                  data-testid="ig-connect-button"
                  onClick={connectIg}
                  className="mt-6 flex items-center gap-3 bg-acid px-10 py-4 font-mono text-xs font-bold uppercase tracking-[0.25em] text-ink transition-colors hover:bg-paper"
                >
                  <Instagram className="h-4 w-4" /> Step 3 — Connect Instagram
                </button>
              )}

              {ig.connected && (
                <div className="mt-6 border border-acid/40 bg-acid/5 p-5">
                  <p data-testid="ig-connected-as" className="font-mono text-[11px] uppercase tracking-[0.2em] text-acid">
                    Connected{ig.username ? ` as @${ig.username}` : ""}
                  </p>
                  {ig.last_sync && (
                    <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.15em] text-paper/50">
                      Last sync: {new Date(ig.last_sync).toLocaleString()}
                    </p>
                  )}
                  <div className="mt-4 flex flex-wrap gap-3">
                    <button
                      type="button"
                      data-testid="ig-sync-now"
                      onClick={syncNow}
                      className="flex items-center gap-2 border border-acid px-5 py-3 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-acid transition-colors hover:bg-acid hover:text-ink"
                    >
                      <RefreshCw className="h-3.5 w-3.5" /> Sync Now
                    </button>
                    <button
                      type="button"
                      data-testid="ig-reconnect"
                      onClick={connectIg}
                      className="border border-white/15 px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-paper/60 transition-colors hover:border-white/40 hover:text-paper"
                    >
                      Reconnect
                    </button>
                  </div>
                </div>
              )}

              {igMsg && (
                <p data-testid="ig-status-msg" className="mt-4 font-mono text-[11px] uppercase tracking-[0.15em] text-acid">
                  {igMsg}
                </p>
              )}
            </div>
            )}
          </div>
        ) : loading ? (
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-paper/50">Loading…</p>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-white/20 p-16 text-center">
            <p className="font-display text-xl font-bold uppercase tracking-tight">
              {tab === "projects"
                ? "No projects yet"
                : tab === "events"
                  ? "No events yet"
                  : "No posts yet"}
            </p>
            <p className="mt-3 text-sm text-fog">
              {tab === "projects"
                ? 'Hit "New Project" to publish your first case study.'
                : tab === "events"
                  ? 'Hit "New Event", paste the official event link and Auto-fill does the typing.'
                  : 'Posted something on Instagram, TikTok or YouTube? Hit "New Post" and paste the link.'}
            </p>
          </div>
        ) : tab === "projects" ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {projects.map((p) => (
              <article
                key={p.id}
                data-testid={`admin-project-card-${p.slug}`}
                className="flex flex-col overflow-hidden border border-white/10 bg-panel"
              >
                <div className="relative">
                  {p.poster ? (
                    <img src={mediaUrl(p.poster)} alt={p.title} className="aspect-video w-full object-cover" />
                  ) : (
                    <div className="flex aspect-video w-full items-center justify-center bg-ink font-mono text-[10px] uppercase tracking-[0.25em] text-paper/30">
                      No image
                    </div>
                  )}
                  <div className="absolute left-3 top-3 flex gap-2">
                    {p.sample && (
                      <span className="bg-acid px-2.5 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-ink">Sample</span>
                    )}
                    {!p.published && (
                      <span className="bg-uv px-2.5 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-paper">Draft</span>
                    )}
                  </div>
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-acid">
                    {p.client || "—"}{p.show ? ` — ${p.show}` : ""}
                  </p>
                  <h2 className="mt-2 font-display text-lg font-bold uppercase tracking-tight">{p.title}</h2>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-paper/40">/work/{p.slug}</p>
                  <div className="mt-5 flex gap-2 border-t border-white/10 pt-4">
                    <button
                      type="button"
                      data-testid={`admin-edit-${p.slug}`}
                      onClick={() => onEdit("project", p.id)}
                      className="flex flex-1 items-center justify-center gap-2 border border-white/15 px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-paper/80 transition-colors hover:border-acid hover:text-acid"
                    >
                      <Pencil className="h-3.5 w-3.5" /> Edit
                    </button>
                    <button
                      type="button"
                      data-testid={`admin-delete-${p.slug}`}
                      onClick={() => del("projects", p, p.title)}
                      className="flex items-center justify-center gap-2 border border-white/15 px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-paper/50 transition-colors hover:border-red-400 hover:text-red-400"
                      aria-label={`Delete ${p.title}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : tab === "events" ? (
          <div data-testid="admin-events-list" className="space-y-4">
            {events.map((ev) => (
              <article
                key={ev.id}
                data-testid={`admin-event-card-${ev.id}`}
                className="flex flex-wrap items-center justify-between gap-4 border border-white/10 bg-panel p-5"
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-display text-lg font-bold uppercase tracking-tight">{ev.name}</p>
                    {ev.attending && (
                      <span className="bg-acid px-2.5 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-ink">
                        Attending{ev.booth ? ` · ${ev.booth}` : ""}
                      </span>
                    )}
                    {!ev.published && (
                      <span className="bg-uv px-2.5 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-paper">Hidden</span>
                    )}
                  </div>
                  <p className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-paper/50">
                    {[ev.start_date || "date TBA", [ev.venue, ev.city].filter(Boolean).join(" · ")].filter(Boolean).join(" — ")}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    data-testid={`admin-edit-event-${ev.id}`}
                    onClick={() => onEdit("event", ev.id)}
                    className="flex items-center gap-2 border border-white/15 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-paper/80 transition-colors hover:border-acid hover:text-acid"
                  >
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </button>
                  <button
                    type="button"
                    data-testid={`admin-delete-event-${ev.id}`}
                    onClick={() => del("events", ev, ev.name)}
                    className="flex items-center justify-center gap-2 border border-white/15 px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-paper/50 transition-colors hover:border-red-400 hover:text-red-400"
                    aria-label="Delete event"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {posts.map((p) => {
              const Icon = PLATFORM_ICONS[p.platform] || Instagram;
              return (
                <article
                  key={p.id}
                  data-testid={`admin-post-card-${p.id}`}
                  className="flex flex-col overflow-hidden border border-white/10 bg-panel"
                >
                  <div className="relative">
                    {p.cover ? (
                      <img src={mediaUrl(p.cover)} alt={p.caption || "Post"} className="aspect-[4/5] w-full object-cover" />
                    ) : (
                      <div className="flex aspect-[4/5] w-full items-center justify-center bg-ink">
                        <Icon className="h-10 w-10 text-paper/20" />
                      </div>
                    )}
                    <div className="absolute left-3 top-3 flex gap-2">
                      <span className="flex items-center gap-1.5 bg-ink/80 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-paper backdrop-blur">
                        <Icon className="h-3 w-3 text-acid" /> {p.platform}
                      </span>
                      {p.sample && (
                        <span className="bg-acid px-2.5 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-ink">Sample</span>
                      )}
                      {p.tagged && (
                        <span className="bg-uv px-2.5 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-paper">Tagged</span>
                      )}
                      {!p.published && (
                        <span className="bg-uv px-2.5 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-paper">Hidden</span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <p className="line-clamp-2 flex-1 text-sm leading-relaxed text-paper/80">{p.caption || "—"}</p>
                    <p className="mt-2 truncate font-mono text-[10px] tracking-[0.1em] text-paper/40">{p.url || "no link"}</p>
                    <div className="mt-5 flex gap-2 border-t border-white/10 pt-4">
                      <button
                        type="button"
                        data-testid={`admin-edit-post-${p.id}`}
                        onClick={() => onEdit("post", p.id)}
                        className="flex flex-1 items-center justify-center gap-2 border border-white/15 px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-paper/80 transition-colors hover:border-acid hover:text-acid"
                      >
                        <Pencil className="h-3.5 w-3.5" /> Edit
                      </button>
                      <button
                        type="button"
                        data-testid={`admin-delete-post-${p.id}`}
                        onClick={() => del("posts", p, p.caption || p.platform)}
                        className="flex items-center justify-center gap-2 border border-white/15 px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-paper/50 transition-colors hover:border-red-400 hover:text-red-400"
                        aria-label="Delete post"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
